import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import TagBadge from '../components/TagBadge';

// ألوان الوسوم تُحفظ في القاعدة كـ hex — لا تستخدم رموز CSS هنا
const PRESETS = ['#0E477A', '#143A67', '#02537D', '#2074A3', '#86BCDC', '#F2CA1B', '#8A6D00', '#2E7D5B', '#D66062', '#6B7686'];

export default function Tags() {
  const { api, user } = useAuth();
  const toast = useToast();
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ name: '', color: PRESETS[5] });
  const [editingId, setEditingId] = useState(null);

  const canEdit = ['admin', 'supervisor'].includes(user?.role);

  const load = () => {
    setLoading(true);
    api('/tags').then(setTags).catch(() => {}).finally(() => setLoading(false));
  };

  useEffect(load, []);

  const save = async () => {
    if (!form.name.trim()) return;
    try {
      if (editingId) {
        await api(`/tags/${editingId}`, { method: 'PUT', body: JSON.stringify(form) });
        toast.success('تم تحديث الوسم');
      } else {
        await api('/tags', { method: 'POST', body: JSON.stringify(form) });
        toast.success('تم إنشاء الوسم');
      }
      setForm({ name: '', color: PRESETS[5] });
      setEditingId(null);
      load();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const startEdit = (t) => {
    setEditingId(t.id);
    setForm({ name: t.name, color: t.color });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setForm({ name: '', color: PRESETS[5] });
  };

  const remove = async (t) => {
    if (!window.confirm(`حذف الوسم "${t.name}"؟ سيُزال من جميع المهام.`)) return;
    try {
      await api(`/tags/${t.id}`, { method: 'DELETE' });
      toast.success('تم حذف الوسم');
      load();
    } catch (err) { toast.error(err.message); }
  };

  return (
    <div>
      <h2 style={{ marginBottom: 20 }}>إدارة الوسوم</h2>

      {canEdit && (
        <div className="card" style={{ marginBottom: 20, padding: 16 }}>
          <div className="card-title" style={{ marginBottom: 12 }}>
            {editingId ? 'تعديل وسم' : 'وسم جديد'}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 12, alignItems: 'end' }}>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label">الاسم</label>
              <input
                className="form-input"
                value={form.name}
                onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                placeholder="مثال: تصميم"
                maxLength={50}
              />
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label">اللون</label>
              <input
                type="color"
                value={form.color}
                onChange={e => setForm(f => ({ ...f, color: e.target.value }))}
                style={{ width: 50, height: 38, padding: 2, border: '1px solid var(--border)', borderRadius: 6, cursor: 'pointer' }}
              />
            </div>
          </div>
          <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
            {PRESETS.map(c => (
              <button
                key={c}
                type="button"
                onClick={() => setForm(f => ({ ...f, color: c }))}
                style={{
                  width: 24, height: 24, borderRadius: '50%', background: c,
                  border: form.color === c ? '2px solid var(--text)' : '2px solid transparent',
                  cursor: 'pointer',
                }}
                aria-label={c}
              />
            ))}
          </div>
          <div style={{ marginTop: 12, display: 'flex', gap: 8, alignItems: 'center' }}>
            <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>معاينة:</span>
            <TagBadge tag={{ name: form.name || 'وسم', color: form.color }} />
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
            <button className="btn btn-primary" onClick={save} disabled={!form.name.trim()}>
              {editingId ? 'حفظ التعديل' : 'إضافة'}
            </button>
            {editingId && <button className="btn btn-outline" onClick={cancelEdit}>إلغاء</button>}
          </div>
        </div>
      )}

      <div className="card">
        <div className="card-title" style={{ marginBottom: 12 }}>الوسوم ({tags.length})</div>
        {loading ? (
          <div className="loader">جاري التحميل...</div>
        ) : tags.length === 0 ? (
          <div className="empty"><div className="empty-icon">🏷️</div>لا توجد وسوم</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {tags.map(t => (
              <div key={t.id} style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: '8px 12px', borderRadius: 6, background: 'var(--gray-50, var(--surface-alt))',
              }}>
                <TagBadge tag={t} size="md" />
                {canEdit && (
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button className="btn btn-sm btn-outline" onClick={() => startEdit(t)}>تعديل</button>
                    <button className="btn btn-sm btn-danger" onClick={() => remove(t)}>حذف</button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
